import { z } from 'zod';

/**
 * A release component as the API returns it.
 *
 * Its own schema, beside `Component` rather than inside it: a release is a
 * separate aggregate on a separate route (`/release-components`), and
 * `GET /components` must never learn about it. Fields that only matter once a
 * later slice reads them (`rowVersion`, the audit stamps) are optional so a
 * server that adds or drops one does not take this page down.
 */
/**
 * One member flag on the detail route. `overridesAtLeast` is a lower bound: the
 * API counts a sparse index, so it can undercount rows written before the index
 * existed. Never present it as exact.
 */
export const ReleaseMember = z.object({
    key: z.string(),
    name: z.string(),
    type: z.enum(['veevart2', 'salesforce']),
    /** The platform-wide default. */
    enabled: z.boolean(),
    archived: z.boolean(),
    overridesAtLeast: z.number().int().nonnegative(),
});
export type ReleaseMember = z.infer<typeof ReleaseMember>;

/**
 * The last manual action through the component, as stored on the release row.
 * The page status renders from this and never from current flag values; it
 * outlives the operation's own rows, which expire.
 */
export const LastAction = z.object({
    operationId: z.string(),
    scope: z.enum(['all', 'clients']),
    enabled: z.boolean(),
    /** Member flags for scope `all`, selected clients for scope `clients`. */
    count: z.number().int().nonnegative(),
    state: z.enum(['running', 'complete', 'partial']),
    finishedAt: z.string().optional(),
});
export type LastAction = z.infer<typeof LastAction>;

export const ReleaseComponent = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    /** The human label, e.g. `2026.10.1`. Not the optimistic-lock counter. */
    releaseVersion: z.string(),
    flagKeys: z.array(z.string()),
    /** The optimistic-lock counter an edit or archive sends back as `expectedRowVersion`. */
    rowVersion: z.number().int().optional(),
    /** Set while an operation holds the release; archive is refused then. */
    activeOperationId: z.string().optional(),
    lastAction: LastAction.optional(),
    /** Detail route only: one row per member flag. Absent on the list. */
    members: z.array(ReleaseMember).optional(),
    createdAt: z.string(),
    createdBy: z.string().optional(),
    updatedAt: z.string().optional(),
    updatedBy: z.string().optional(),
});
export type ReleaseComponent = z.infer<typeof ReleaseComponent>;

export const ReleaseComponentListResponse = z.object({ releases: z.array(ReleaseComponent) });
